/**
 * TextMatchesBar — every spelling a fuzzy text search used, and the control to strike them out.
 *
 * Fuzzy matching is only safe to default to "loose" because NOTHING IS MATCHED SILENTLY. Typing
 * "badal" also finds "vadal" and "kajal"; this bar shows all three with their counts, and one
 * click strikes out the wrong one. A struck spelling stays on the bar, crossed out, so it can be
 * put back — a strike-out that vanished would be a filter the operator could no longer see.
 *
 * The numbers are the server's, counted in the query's own scope (window, cameras, models), so
 * "kajal 3" means striking it removes about three results — not three somewhere on the estate.
 */
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import type { SearchFuzziness, SearchTextReport } from '../../api/contracts';

const FUZZINESS: Array<{ value: SearchFuzziness; label: string; hint: string }> = [
  { value: 'exact', label: 'Exact', hint: 'Only the spelling typed (or words starting with it)' },
  { value: 'close', label: 'Close', hint: 'About one mistake — badal finds vadal' },
  { value: 'loose', label: 'Loose', hint: 'About two mistakes — badal finds vadal and kajal' },
];

const KIND_TITLE: Record<string, string> = {
  exact: 'Exactly what was typed',
  prefix: 'Starts with what was typed',
  fuzzy: 'A close spelling',
};

export interface TextMatchesBarProps {
  report: SearchTextReport[] | undefined;
  /** Spellings currently struck out (lower-cased words). */
  excluded: string[];
  fuzziness: SearchFuzziness;
  onToggle: (term: string) => void;
  onFuzziness: (f: SearchFuzziness) => void;
}

export default function TextMatchesBar({ report, excluded, fuzziness, onToggle, onFuzziness }: TextMatchesBarProps) {
  const struck = new Set(excluded.map((e) => e.toLowerCase()));
  const tokens = (report || []).flatMap((c) => c.tokens);

  return (
    <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1.5 border-b border-border bg-card px-4 py-2 text-xs">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Match</span>
        <div className="flex overflow-hidden rounded-md border border-border" role="radiogroup" aria-label="Spelling tolerance">
          {FUZZINESS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="radio"
              aria-checked={fuzziness === f.value}
              title={f.hint}
              onClick={() => onFuzziness(f.value)}
              className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${
                fuzziness === f.value ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {tokens.map((t, i) => (
        <div key={`${t.token}-${i}`} className="flex flex-wrap items-center gap-1">
          <span className="text-muted-foreground">
            “<span className="font-medium text-foreground">{t.token.toLowerCase()}</span>”
            {t.matches.length > 0 && ' →'}
          </span>
          {t.unmatched && (
            <span className="flex items-center gap-1 text-amber-500">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              {t.matches.length ? 'every spelling struck out' : 'nothing close to it in this window'}
            </span>
          )}
          {t.matches.map((m) => {
            const off = m.excluded || struck.has(m.term.toLowerCase());
            return (
              <button
                key={m.term}
                type="button"
                onClick={() => onToggle(m.term)}
                title={`${KIND_TITLE[m.kind] || ''}${m.kind === 'fuzzy' ? ` (${m.distance} edit${m.distance === 1 ? '' : 's'} away)` : ''}`
                  + ` — found as ${m.fields.join(' / ')}. ${off ? 'Click to include it again.' : 'Click to strike it out.'}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors ${
                  off
                    ? 'border-border text-muted-foreground line-through opacity-70 hover:opacity-100'
                    : m.kind === 'fuzzy'
                      ? 'border-amber-500/60 text-foreground hover:bg-muted'
                      : 'border-brand-ring bg-brand-lighter text-foreground hover:bg-muted'
                }`}
              >
                <span>{m.fields.includes('plate') ? m.term.toUpperCase() : m.term}</span>
                <span className="tabular-nums text-muted-foreground">{m.events}</span>
                <span aria-hidden className="text-muted-foreground">{off ? '+' : '×'}</span>
              </button>
            );
          })}
          {t.matches_total > t.matches.length && (
            <span className="text-muted-foreground">+{t.matches_total - t.matches.length} more</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * `text` with every matched word highlighted — how a card shows WHICH word made it a result.
 *
 * Whole words only, case-insensitive, so "badal" lights up in "Badal Tea Stall" but not inside
 * "Badalpur". Plates are compared with separators ignored, because the stored plate has none and
 * the raw read ("GJ182 / T3877") is full of them.
 */
export function highlightWords(text: string, terms: string[]): ReactNode {
  if (!text || !terms.length) return text;
  const wanted = new Set(terms.map((t) => t.toLowerCase()));
  const parts = text.split(/([\p{L}\p{N}]+)/u);
  return parts.map((part, i) => (
    wanted.has(part.toLowerCase())
      // eslint-disable-next-line react/no-array-index-key
      ? <mark key={i} className="rounded bg-brand-light px-0.5 text-foreground">{part}</mark>
      : part
  ));
}

/** Every spelling the answer is matching on (struck-out ones excluded), for `highlightWords`. */
export function activeSpellings(report: SearchTextReport[] | undefined): string[] {
  return (report || []).flatMap((c) => c.tokens.flatMap((t) => t.matches
    .filter((m) => !m.excluded)
    .map((m) => m.term)));
}
